'use client';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { isUndefined } from 'lodash';
import { Button as RACButton } from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { ScreenSizeContext } from '@/context';
import { focusRing } from '@/utils';
import { ArrowLeft, ArrowRight, MoreHorizontal } from '../icons';
import { CTAButton } from './Button';

const MOBILE_SETTINGS: PaginationSettings = {
	maxVisibleSlots: 5,
	maxFirstSlots: 2,
};
const DESKTOP_SETTINGS = {
	maxVisibleSlots: 7,
	maxFirstSlots: 3,
};

/**
 * Pagination for Accordion props
 */
interface PaginationProps {
	/**
	 * Total counts of items that will be displayed
	 */
	itemCounts: number;
	/**
	 * To calculate chosen page to be displayed
	 * If provided, this will be the default page to be rendered
	 */
	paginationOffset?: number;
	/**
	 * Max amount of items to be displayed on one page
	 */
	paginationLimit?: number;
	/**
	 * Click handler
	 */
	handleClick?: (value: number) => void;
	/**
	 * Pagination settings specific to mobile view
	 */
	mobileSettings?: PaginationSettings;
	/**
	 * Pagination settings specific to desktop view
	 */
	desktopSettings?: PaginationSettings;
	variant?: 'primary' | 'secondary';
	theme?: 'light' | 'dark';
	className?: string;
}

interface PaginationSettings {
	/**
	 * The maximum number of pagination slots visible at once
	 */
	maxVisibleSlots: number;
	/**
	 * The maximum number of slots shown at the beginning of pagination
	 */
	maxFirstSlots: number;
}

const ELLIPSIS = '...';
type PageItem = number | string;

export const pagination = tv({
	extend: focusRing,
	base: [
		'inline-flex items-center justify-center gap-2',
		'h-8 w-8 cursor-pointer rounded-xs text-center text-sm text-nowrap transition',
	],
	variants: {
		selected: {
			false: 'border-1 hover:border-2 active:border-2',
		},
	},
	compoundVariants: [
		{
			selected: false,
			theme: 'light',
			class: ['border-light-border-alt text-light-link hover:border-light-action-secondary'],
		},
		{
			selected: false,
			theme: 'dark',
			class: ['border-dark-border-alt text-dark-link hover:border-dark-action-secondary'],
		},
		{
			selected: true,
			theme: 'light',
			class: [
				'bg-light-action-primary text-light-link-on-action hover:bg-light-action-primary-hover font-bold',
			],
		},
		{
			selected: true,
			theme: 'dark',
			class: ['bg-dark-action-primary text-dark-link hover:bg-dark-action-primary-hover font-bold'],
		},
	],
	defaultVariants: {
		selected: false,
		theme: 'light',
	},
});

/**
 * UI element that divide list of items into pages
 * @param {PaginationProps} Props
 * @param {PaginationSettings} mobileSettings Default: { maxVisibleSlots: 5, maxFirstSlots: 2 }
 * @param {PaginationSettings} desktopSettings Default: { maxVisibleSlots: 7, maxFirstSlots: 3 }
 * @returns a series of numbers that indicates the current page of the items
 */
export const Pagination = ({
	itemCounts,
	paginationOffset = 0,
	paginationLimit = 1,
	handleClick,
	mobileSettings = MOBILE_SETTINGS,
	desktopSettings = DESKTOP_SETTINGS,
	theme,
	className,
}: PaginationProps) => {
	const nPages = Math.ceil(itemCounts / paginationLimit);
	const selectedPage = calculateSelectedPage(paginationOffset, paginationLimit);
	const [curPage, setCurPage] = useState<number>(selectedPage);
	const { isSmScreen: isMobileSize } = useContext(ScreenSizeContext);

	// Memo
	const slots = useMemo(() => {
		const settings = isMobileSize ? mobileSettings : desktopSettings;
		return calculateSlots(curPage, nPages, settings);
	}, [isMobileSize, curPage, nPages]); // eslint-disable-line react-hooks/exhaustive-deps

	// Life cicle
	useEffect(() => {
		if (!isUndefined(selectedPage)) setCurPage(selectedPage);
	}, [selectedPage]);

	// Helpers
	function calculateSelectedPage(offset: number, limit: number) {
		if (limit == 0) {
			return 1;
		}
		return offset / limit + 1;
	}

	const handleButtonClick = (targetPage: PageItem) => {
		const page = Number(targetPage);
		if (page <= 0 || page > nPages) {
			return;
		}
		setCurPage(page);
		if (handleClick) handleClick(page);
	};

	function calculateSlots(selected: number, total: number, settings: PaginationSettings) {
		const { maxVisibleSlots, maxFirstSlots } = settings;
		const nSlotsAside = maxVisibleSlots - 2; // subtract ellipsis (1) and slot (1) from one side
		const nSlotsMiddle = maxVisibleSlots - 4; // subtract from both sides (x2)

		const hasFewerPages = total <= maxVisibleSlots;
		const isFirstsSelected = selected <= maxFirstSlots;
		const isMiddleSelected = !isFirstsSelected && selected <= total - maxFirstSlots;
		const isLastsSelected = selected > total - maxFirstSlots;
		const slotsInMiddle = Array.from(
			{ length: nSlotsMiddle },
			(_, i) => selected - Math.floor(nSlotsMiddle / 2) + i,
		);

		let result: Array<PageItem> = [];
		if (hasFewerPages) {
			result = Array.from({ length: total }, (_, i) => i + 1);
		} else if (isFirstsSelected) {
			result = [...Array.from({ length: nSlotsAside }, (_, i) => i + 1), ELLIPSIS, total];
		} else if (isMiddleSelected) {
			result = [1, ELLIPSIS, ...slotsInMiddle, ELLIPSIS, total];
		} else if (isLastsSelected) {
			result = [
				1,
				ELLIPSIS,
				...Array.from({ length: nSlotsAside }, (_, i) => total - nSlotsAside + i + 1),
			];
		}

		return result;
	}

	// Layout
	const Slots = () => {
		return (
			<div className="flex items-center">
				{slots.map((num, index) => (
					<Slot pageItem={num} index={index} key={index} />
				))}
			</div>
		);
	};

	const Slot = ({ pageItem, index }: { pageItem: PageItem; index: number }) => {
		return pageItem === ELLIPSIS ? (
			<li className="my-[8px] mr-[12px]" key={`ellipsis-${pageItem}`}>
				<span className="flex h-8 w-8 items-center justify-center">
					<MoreHorizontal />
				</span>
			</li>
		) : (
			<li className="mr-[12px]" key={`page-${index}`}>
				<RACButton
					key={pageItem}
					className={pagination({
						selected: pageItem === curPage,
						theme: theme,
						className: className,
					})}
					onPress={() => handleButtonClick(pageItem)}
				>
					<a>{pageItem}</a>
				</RACButton>
			</li>
		);
	};

	return (
		<nav aria-label="pagination">
			<ul className="flex list-none flex-wrap">
				<li
					className={`my-[8px] mr-[12px] h-8 ${curPage == 1 ? 'hidden' : 'initial'}`}
					key="page-back"
				>
					<CTAButton
						className="h-8 w-auto px-[10px]!"
						theme={theme}
						onPress={() => handleButtonClick(curPage - 1)}
						leftIcon={<ArrowLeft className="size-icon-md text-light-action-secondary" />}
					>
						<span className="hidden md:block">Back</span>
					</CTAButton>
				</li>
				<Slots />
				<li className={`my-[8px] h-8 ${curPage == nPages ? 'hidden' : 'initial'}`} key="page-next">
					<CTAButton
						className="h-8 w-auto px-[10px]!"
						theme={theme}
						onPress={() => handleButtonClick(curPage + 1)}
						rightIcon={<ArrowRight className="size-icon-md text-light-action-secondary" />}
					>
						<span className="hidden md:block">Next</span>
					</CTAButton>
				</li>
			</ul>
		</nav>
	);
};
